import { Component, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { Utils } from '../../../../../Utils/utils';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType, SrIOName, UserType } from '../../../../../shared/constants/services.enum';
import { ApiEndPoints, ApiRoot } from '../../../../../shared/constants/api-endpoints';
import { RoutesPath } from '../../../../../shared/constants/routes-path';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '../../../../../shared/services/shared.service';
import { ApiService } from '../../../../../core/services/api.service';
import { TextFieldComponent } from '../../../../../shared/components/controls/text-field/text-field.component';
import { OptionFieldComponent } from '../../../../../shared/components/controls/option-field/option-field.component';
import { MembershipCancellationRequest } from '../../../../../shared/models/services-request/membership-cancellation.model';
import { SrSubmitSuccessModalComponent } from "../../../../../shared/components/sr-submit-success-modal/sr-submit-success-modal.component";
import { SkeletonLoaderComponent } from "../../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { SrStatus } from '../../../../../shared/constants/status.enum';
import { ActiveMembershipsComponent } from "../../../../../shared/components/active-memberships/active-memberships.component";
import { FileComponent } from '../../../../../shared/components/controls/file/file.component';
import { FolderResponse, AllowedDocumentType } from '../../../../../shared/models/documents-response.model';
import { PendingSrModalComponent } from "../../../../../shared/components/pending-sr-modal/pending-sr-modal.component";
import { DcMethodValues } from '../../../../../shared/constants/dc-method.constants';
import { customValidator, normalizeIban } from '../../../../../Utils/validator';

@Component({
  selector: 'app-membership-cancellation',
  imports: [TranslateModule, ReactiveFormsModule, SHARED_COMPONENTS, SrSubmitSuccessModalComponent, SkeletonLoaderComponent, ActiveMembershipsComponent, PendingSrModalComponent],
  templateUrl: './membership-cancellation.component.html',
  styleUrl: './membership-cancellation.component.scss'
})
export class MembershipCancellationComponent {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  @ViewChildren(TextFieldComponent) textFields!: QueryList<TextFieldComponent>;
  @ViewChildren(OptionFieldComponent) optionFields!: QueryList<OptionFieldComponent>;
  requestPayload: MembershipCancellationRequest = new MembershipCancellationRequest();
  documentServiceType = DocumentServiceType;
  private readonly accountDetailsDocName = 'co account details-on bank letterhead';

  allFields: any = "";
  form: FormGroup;
  totalSteps: number = 3;
  step: any = 1;
  LookupKey = LookupKey;
  membershipId: any = "";
  memberships = [];
  accountId: any = "";
  userCSN: any = "";
  lang: string = "en";
  userType: string = "";
  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;
  // This method is used to initialize the class instance using fb, router, route, shared service, and api
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private api: ApiService
  ) {
    this.userType = this.sharedService.getPrimaryAccount()['Account Type Code'];
    this.lang = this.sharedService.getCurrentLang();
    this.accountId = this.sharedService.getCsnAccountId('id');
    this.userCSN = this.sharedService.getCsnAccountId('csn');
    this.form = this.fb.group({
      membershipId: [""],
      cancellationReason: [null, [Validators.required, Validators.maxLength(150)]],
      cancellationLetter: [null, [Validators.required, Validators.maxLength(100)]],
      cancellationReasonDescription: ['', [Validators.maxLength(30)]],
      serviceBalance: [{ value: '0', disabled: true }],
      bankName: [null, [Validators.maxLength(150)]],
      accountHolderName: ['', [Validators.maxLength(100)]],
      IBANNo: ['', [Validators.maxLength(34), customValidator('iban')]],
      docLicenseCancelCertificate: [],
      docAccountDetail: [],
      docLetterFromCompany: []
    });

    this.form.get('IBANNo')?.valueChanges.subscribe((value: string | null) => {
      const normalizedValue = normalizeIban(value);
      if (value !== normalizedValue) {
        this.form.get('IBANNo')?.setValue(normalizedValue, { emitEvent: false });
      }
    });

    this.setInitialValues();
    this.updateBankFieldValidators();

    
    this.sharedService.getSrNumber(this.route).subscribe(srNumber => {
      if (srNumber) {
        this.srNumber = srNumber;
        this.getSRDetail();
      }
    });
    // in case of draft
    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber;
      this.getSRDetail();
    }
    if(!this.srNumber){
      this.getMemberDetail();
    }
    this.checkForPendingSr();
  }

  // This method is used to set initial values
  setInitialValues() {
    this.getActiveMembership();
    //only for Member
    if (this.userType != UserType.MEMBER) {
      this.sharedService.toastMessage('INFO', 'ONLY_MEMBER_ALLOWED', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return
    }
    if (this.memberships.length < 1) {
      this.sharedService.toastMessage('INFO', 'NO_ACTIVE_MEMBERSHIP', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return
    }

  }

  pendingSrNumber: string = "";
  pendingSrDetail: any = "";
  srDetailLoading: boolean = false;
  // This method is used to check for pending sr
  checkForPendingSr() {
    if (this.srNumber == "" && this.userCSN) {
      this.srDetailLoading = true;
      this.sharedService.checkInprogressApplication(this.userCSN, ServiceName.MEMBERSHIP, ServiceName.MEMBERSHIP_CANCELLATION)
        .subscribe({
          // This method is used to handle the next callback using response
          next: (response: any) => {
            if (response["Error Code"] == null && response["Error Message"] == null && response?.SiebelMessage['DC Community Portal SR']) {
              const srData = response?.SiebelMessage['DC Community Portal SR'];
              const srRecords = Array.isArray(srData) ? srData : [srData];
              const pendingSrNumbers = srRecords
                .map((sr: any) => sr?.['SR Number'])
                .filter(Boolean);
              if (pendingSrNumbers.length) {
                this.pendingSrNumber = pendingSrNumbers.join(', ');
                this.pendingSrDetail = Array.isArray(srData) ? srRecords : srData;
                // this.sharedService.toastMessage('INFO', 'MANGE_SIGNATORY_SR_PENDING', 'info');                
                return;
              }
            } else {
              this.srDetailLoading = false;
              this.getMemberDetail();
            }
          }
        })
    }
  }
  // This method is used to get active membership
  getActiveMembership() {
    this.membershipId = this.userCSN;
    let userDetails: any = this.sharedService.getUserProfile();
    let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
    const filteredAccounts: any = accounts.filter((acc: any) => {
      // must have CSN
      if (!acc.CSN || acc.CSN.trim() === '' || acc["DC Expiry Date"] === "" || !acc["DC Expiry Date"]) {
        return false;
      }
      // use common method for membership expiry validation
      return true; //Utils.isMembershipValid(acc["DC Expiry Date"]);
    });
    this.memberships = filteredAccounts;

  }

  // This method is used to set selected member ship using member
  setSelectedMemberShip(member: any) {
    let memberCSN = member.CSN;
    this.membershipId = memberCSN;
    this.userCSN = memberCSN;
    this.sharedService.setPrimaryAccount(member);
    this.form.get('membershipId')?.setValue(memberCSN);
  }

  // This method is used to update bank field validators
  updateBankFieldValidators() {
    const balanceValue = parseFloat(this.form.get('serviceBalance')?.value || '0');

    const bankName = this.form.get('bankName');
    const accountHolderName = this.form.get('accountHolderName');
    const IBANNo = this.form.get('IBANNo');
    const docAccountDetail = this.form.get('docAccountDetail');


    if (balanceValue > 0) {
      bankName?.setValidators([Validators.required, Validators.maxLength(150)]);
      accountHolderName?.setValidators([Validators.required, Validators.maxLength(100)]);
      IBANNo?.setValidators([Validators.required, Validators.maxLength(34), customValidator('iban')]);
      // docAccountDetail?.setValidators([Validators.required, Validators.maxLength(50)]);
    } else {
      bankName?.setValidators([Validators.maxLength(150)]);
      accountHolderName?.setValidators([Validators.maxLength(100)]);
      IBANNo?.setValidators([Validators.maxLength(34), customValidator('iban')]);
      docAccountDetail?.setValidators([Validators.maxLength(50)]);
    }

    bankName?.updateValueAndValidity();
    accountHolderName?.updateValueAndValidity();
    IBANNo?.updateValueAndValidity();
    docAccountDetail?.updateValueAndValidity();

  }
  // This method is used to handle lov change event using e
  onLovChange(e: any) {

  }
  selectMembershipError: boolean = false;
  // This method is used to proceed
  proceed() {
    if (this.membershipId) {
      this.checkForPendingSr();
      this.step = 2;
    } else {
      this.selectMembershipError = true;
    }
  }
  memberDetail: any;
  licenseCategory: string = "";

  // This method is used to get member detail
  getMemberDetail() {
    if (!this.userCSN) {
      return;
    }
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Member Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.MEMBERSHIP_CANCELLATION,
        "IOName": "DC Get Acc Details IO",
        "CSN": this.userCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_MEMBER_DETAIL_BY_CSN,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if (response["Error Code"] == "" && response["Error Message"] == "" && response["SiebelMessage"]) {
          if (response["SiebelMessage"]['DC Account Data Transfer']) {
            this.memberDetail = response["SiebelMessage"]['DC Account Data Transfer'];
            this.licenseCategory = this.memberDetail["DC License Category"];

            if (this.memberDetail && this.memberDetail["Organization Payment Profile"]) {
              let balanceAmount = Utils.getAdvancePaymentBalance(this.memberDetail["Organization Payment Profile"]);
              this.form.get('serviceBalance')?.setValue(balanceAmount);
              this.updateBankFieldValidators();
            }
            this.openFolder();
          }
        } else {
          this.sharedService.toastMessage("error", '', 'error', response["Error Message"]);
        }
      },
      error: () => {
      }
    })
  }

  //get folder id and attachments
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];
  documentLoader: boolean = false;
  // This method is used to open folder
  openFolder() {
    this.documentLoader = true;
    let isLabels = !this.srNumber; // true if srNumber is empty
    this.sharedService.getDocuments(this.srNumber, DocumentServiceType.MEM_CANCEL, isLabels, { licenseType: this.licenseCategory = FieldValue.FREE_ZONE ? DocumentServiceType.LIC_CATG_FZ : DocumentServiceType.LIC_CATG_NFZ })
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          this.documentLoader = false;
          if (response.success) {
            // If SR exists → response.data, otherwise → response
            this.srFolder = response.data ? response.data : response;
            // this.srFolder.allowedDocumentTypes = this.srFolder.allowedDocumentTypes ?? [];            
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes.filter((d: any) => !d.forUISection);
            this.applyConditionalDocumentRules();
          } else {
            this.sharedService.toastMessage("error", response.message, 'error');
          }
        },
        // This method is used to handle the error callback using err
        error: err => {
          this.documentLoader = false;
          this.sharedService.toastMessage("error", err, 'error');
        }
      });
  }
  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    // If there are no docs to check, nothing is missing
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }
    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    const balanceValue = parseFloat(this.form.get('serviceBalance')?.value ?? '0');
    const shouldRelaxAccountDoc = !isNaN(balanceValue) && balanceValue < 1;

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      // Safety guard; should not happen due to length check above
      if (!cmp) continue;

      const docName = (doc?.nameEn || '').trim().toLowerCase();
      if (shouldRelaxAccountDoc && docName === this.accountDetailsDocName) {
        if (typeof cmp.checkMissing === 'function') {
          cmp.checkMissing(false);
        }
        continue;
      }

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }

  private applyConditionalDocumentRules(): void {
    const balanceValue = parseFloat(this.form.get('serviceBalance')?.value ?? '0');
    if (isNaN(balanceValue) || balanceValue >= 1) {
      return;
    }

    const normalizeName = (doc?: AllowedDocumentType) => (doc?.nameEn || '').trim().toLowerCase();

    if (this.srFolder?.allowedDocumentTypes?.length) {
      this.srFolder.allowedDocumentTypes = this.srFolder.allowedDocumentTypes.map(doc =>
        normalizeName(doc) === this.accountDetailsDocName ? { ...doc, mandatory: false } : doc
      );
    }

    if (this.mainFromSectionDocs?.length) {
      this.mainFromSectionDocs = this.mainFromSectionDocs.map(doc =>
        normalizeName(doc) === this.accountDetailsDocName ? { ...doc, mandatory: false } : doc
      );
    }
  }
  // This method is used to move to the next step
  nextStep() {
    const stepTwoFields =
      this.step === 2 || this.step === 3
        ? ['cancellationReason', 'cancellationLetter', 'cancellationReasonDescription', 'bankName', 'accountHolderName', 'IBANNo']
        : [];

    if (this.textFields && this.optionFields) {
      this.allFields = [...this.textFields.toArray(), ...this.optionFields.toArray()];
    }
    const formFieldData = this.form.getRawValue();
    if (this.step > 2) {
      this.isFromSumit = true;
    }
    if (Utils.handleInvalidStep(this.form, this.allFields, stepTwoFields)) return;

    if (this.step == 2) {
      this.submitSrData(formFieldData, SrActionType.DRAFT);
    }
    if (this.step == 3) {
      if (this.form.invalid) {
        const invalidControls = Utils.getInvalidControls(this.form);
        console.warn('Invalid controls:', invalidControls);
        this.form.markAllAsTouched();
        Utils.focusFirstInvalidControl(this.form, this.allFields);
        this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
        return;
      } else {
        this.submitSrData(formFieldData, SrActionType.SUBMIT);
      }
    }
    if (this.step < 3) {
      this.step = this.step + 1;
    }
  }
  isFromSumit: boolean = false;
  // This method is used to save and close
  saveAndClose(): void {
    const formFieldData = this.form.getRawValue();
    this.submitSrData(formFieldData, SrActionType.SAVE);
  }

  srNumber: string = "";
  srAccountId: string = "";
  successModal: boolean = false;
  formSubmitLoader: boolean = false;
  isSaved: boolean = false
  showActions: boolean = true;

  // get DET license detail
  loadingSrDeetail: boolean = false;
  // This method is used to get sr detail
  getSRDetail() {
    this.loadingSrDeetail = true;
    const requestPayload = {
      "body": {
        "ProcessName": "DC Get SR Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.MEMBERSHIP_CANCELLATION,
        "SR Number": this.srNumber,
        "IOName": SrIOName.MEMBERSHIP
      }
    };
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_SR_DETAIL,
      requestPayload
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        this.loadingSrDeetail = false;
        if (response?.SiebelMessage) {
          const serviceRequest = response?.SiebelMessage["DC Mem SR"];
          this.accountId = serviceRequest['Account Id'];
          this.userCSN = serviceRequest['CSN'];
          this.getMemberDetail();
          this.form.patchValue({
            cancellationReason: serviceRequest["DC Cancel Reason"],
            cancellationLetter: serviceRequest["DC Cancel Letter Req"],
            cancellationReasonDescription: serviceRequest["Reason"],
            bankName: serviceRequest["Bank Name"],
            accountHolderName: serviceRequest["DC Account Holder"],
            IBANNo: normalizeIban(serviceRequest["DC IBAN Num"])
          })

          if (this.sharedService.enableNotesOption(serviceRequest["Status"])) {
            this.srInprgoressAwating = true;
            this.showNotes = true;
          }

          if (serviceRequest["Status"] != SrStatus.Draft && serviceRequest["Status"] != SrStatus.AwaitingClarification) {
            this.showActions = false;
            this.form.disable(); // disabled all fields in the form
          }
          this.step = 3;
        }
      },
      // This method is used to handle the error callback
      error: () => {
        this.loadingSrDeetail = false;
      }
    })
  }
  technicalErrorMsg: boolean = false;
  // This method is used to submit sr data using data and action type
  submitSrData(data: any, actionType: string) {
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);

    let isActionSubmit = actionType == SrActionType.SUBMIT;
    let isActionSave = actionType == SrActionType.SAVE;
    let isActionDraft = actionType == SrActionType.DRAFT;

    if (isActionSubmit && this.checkRequiredDocuments()) {
      this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }
    let srSubType = ServiceName.MEMBERSHIP_CANCELLATION;
    this.requestPayload.body.DCMethod = isActionSubmit ? 'SUBMIT' : 'SAVEASDRAFT';
    this.requestPayload.body.SiebelMessage.MessageId = Utils.generateUniqueId();

    const sr = this.requestPayload.body.SiebelMessage.ListOfDCMemSRIO["DC Mem SR"];
    sr["DC Channel"] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;
    sr['SR Type'] = ServiceName.MEMBERSHIP;
    sr['SR Sub Type'] = ServiceName.MEMBERSHIP_CANCELLATION;
    sr["DC Member Name(Arabic)"] = this.memberDetail?.["DC Name Arabic"] ?? "";
    sr["DC Member Name(English)"] = this.memberDetail?.["DC Name English"] ?? "";
    sr["DC Cancel Reason"] = data.cancellationReason ?? "";
    sr["DC IBAN Num"] = normalizeIban(data.IBANNo);
    sr["Bank Name"] = data.bankName ?? "";
    sr["DC Account Holder"] = data.accountHolderName ?? "";
    sr["Reason"] = data.cancellationReasonDescription ?? "";
    sr["DC Cancel Letter Req"] = data.cancellationLetter ?? "";
    sr['SR Number'] = this.srNumber ?? "";
    sr['Account Id'] = this.accountId ?? "";

    this.formSubmitLoader = !isActionDraft ? true : false;
    this.technicalErrorMsg = false;
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.CREATE_UPDATE_SR,
      this.requestPayload
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if (response['Error Code'] == "" && response['Error Message'] == "" && response["SR Number"]) {
          this.isSaved = true;

          if (isActionSave || isActionDraft) {
            if (isActionDraft && this.srNumber == "") {
              this.srNumber = response["SR Number"];
              this.srAccountId = response['Account Id'];
              sessionStorage.setItem("srNumber", response["SR Number"]);
            } else {
              this.srNumber = response["SR Number"];
              this.srAccountId = response['Account Id'];
              sessionStorage.setItem("srNumber", response["SR Number"]);
              if (isActionSave) {
                this.successModal = true;
              }
            }
            this.openFolder();
          } else {
            //set sr detail for success page to get ioname
            this.srNumber = response["SR Number"];
            let lang: any = this.sharedService.getCurrentLang();

            const { service, serviceAr } = Utils.getServiceLabels(srSubType, lang);
            let srDetailObj = {
              serviceSubTypeEn: service,
              serviceSubTypeAr: serviceAr,
              service: srSubType,
              IOName: SrIOName.MEMBERSHIP,
              srNumber: this.srNumber,
              totalSteps: this.totalSteps,
              step: this.step
            }
            this.sharedService.setSrResponse(srDetailObj);

            this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
            sessionStorage.removeItem("srNumber");
            this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
          }
          this.formSubmitLoader = false;
        } else {
          this.formSubmitLoader = false;
          this.technicalErrorMsg = true;
        }
      },
      // This method is used to handle the error callback
      error: () => {
        this.isSaved = true;
        this.formSubmitLoader = false;
        this.technicalErrorMsg = true;
      }
    })
  }

  // This method is used to calculate form progress
  get formProgress() {
    if (this.form.disabled) {
      return 100;
    }
    if (!this.form.invalid) {
      if (!this.checkRequiredDocuments()) {
        return 100;
      }
    }
    return Utils.calculateFormProgress(this.step, this.totalSteps)
  }

  //Unsave form confirmation
  // This method is used to determine if deactivate
  canDeactivate(): boolean {
    return this.sharedService.confirmNavigationIfFormDirty(this.form, this.isSaved);
  }

  // This method is used to close success modal
  closeSuccessModal() {
    this.successModal = false;
    sessionStorage.removeItem("srNumber");
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // method for updae attachment
  // handelFileEvent(document: any, documentField: any) {
  //   let documents = Utils.checkIsArray(document);
  //   Utils.setControlValidityBasedOnDocument(this.form, documentField, documents);
  // }

  // This method is used to handel file event using document and document field
  handelFileEvent(document: any, documentField: any) {

  }
  // This method is used to close pending sr modal
  closependingSrModal() {
    this.pendingSrNumber = "";
    // this.sharedService.gotoDetailPage(this.pendingSrDetail);
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    sessionStorage.removeItem("srNumber");
  }
}
